import React, { useCallback } from "react"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import Button from "react-bootstrap/Button"
import Row from "react-bootstrap/Row"
import * as modules from "../../models/modules"
import { Type } from "shared/types/moduleTypes"
import { parseFormData } from "../../helpers/formDataHelper"
import Assessment from "./Assessment"
import AssignmentProgress from "./AssignmentProgress"
import styles from "./styles.module.scss"
import { AuthContext } from "AuthContext"
import { checkIfAssessmentComplete, getUnansweredQuestions, saveDemoAssessmentResponses } from "./helpers/assessmentHelper"
import { Modal } from "react-bootstrap"
import { AssignmentContext } from "./AssignmentContext"
import { saveLessonInterval } from "components/Lessons/LessonModule/helpers/lessonHelper"
import { AssignmentTopicContext } from "./AssignmentTopicContext"
import { DrawingAnswer } from "shared/types/studentTypes"
import { BeatLoader } from "react-spinners"
import { formatToMySQLDateTime } from "shared/helpers/formatDateHelper"
import { BlocksWithSTEMCopyright } from "shared/types/science35Types"

interface AssessmentProps {
    assessmentId: string
    moduleName: string,
    assessmentNumber: number,
    type: Type,
    submissionId?: string
    requireCompletion?: boolean
    submitted: () => void
    isPartOfTopic: boolean
    initialAssessmentData?: Record<string, string>
    initialAssessmentDrawingData?: Record<string, DrawingAnswer>
    setInitialAssessmentDrawingData?: React.Dispatch<React.SetStateAction<Record<string, DrawingAnswer>>>
    oneQuestionMode: boolean
    randomizable: boolean
   isReview: boolean
}

export default function AssignmentAssessment({ assessmentId, moduleName, assessmentNumber, type, submissionId, requireCompletion, submitted, isPartOfTopic, initialAssessmentData, initialAssessmentDrawingData, setInitialAssessmentDrawingData, oneQuestionMode, randomizable, isReview }: AssessmentProps) {
    const authContext = React.useContext(AuthContext)
    const { assessmentOnly, demoMode, block } = React.useContext(AssignmentContext)
    const firstLoadTime = useRef(null)
    const [showModal, setShowModal] = useState(false)
    const [unanswered, setUnanswered] = useState<string[]>(null)
    const isTeacher = authContext.isLoggedIn && !authContext.isStudent
    const [submitting, setSubmitting] = useState(false)

    const { assessmentDrawResponses: topicDrawResponses, setAssessmentDrawResponses: setTopicDrawResponses } = React.useContext(AssignmentTopicContext)
    const [standaloneDrawResponses, setStandaloneDrawResponses] = useState(initialAssessmentDrawingData ?? {})
    const [ drawingResponses, setDrawingResponses ] = setTopicDrawResponses ?
        [ topicDrawResponses, setTopicDrawResponses ] :
        [ standaloneDrawResponses, setStandaloneDrawResponses ]

    const saveAssessmentTimerRef = useRef(new Date())
    const [submitEnabled, setSubmitEnabled] = useState(true)

    const submitAssessment = async () => {
        if (isTeacher && !demoMode) {
            return submitted()
        }
        const form = document.getElementById("assessment-form") as HTMLFormElement
        if (requireCompletion) {
            const formCompleteCheck  = checkIfAssessmentComplete(form, Object.keys(drawingResponses).length)
            if (!formCompleteCheck[0]) {
                return toast.error(formCompleteCheck[1])
            }
        }
        setSubmitting(true)

        if (!form) {
            return toast.error("There was an error loading the assessment. Please refresh the page and try again.")
        }
        
        if (demoMode) {
            setSubmitting(false)
            return submitted()
        }
        
        const postParams = {...parseFormData(form, false), ...drawingResponses, startTime: firstLoadTime.current}

        try {
            await modules.submitAssessment({
                moduleName: moduleName,
                assessmentType: type,
                assessmentNumber: assessmentNumber,
                submissionId: submissionId,
                postParams: postParams
            }).then((res) => {
                if (res.valid) {
                    toast.success(res.message)
                } else {
                    toast.error(res.message)
                }
            }).catch((err) =>
                toast.error(err.response?.data?.message ?? "There was an error submitting the assessment. Please try again.")
            )
            submitted()
        } catch (err) {
            toast.error(
                err?.response?.data?.message ?? "There was an error submitting the assessment. Please try again."
            )
        }
        setSubmitting(false)
    }

    useEffect(() => {
        if (!firstLoadTime.current) {
            firstLoadTime.current = formatToMySQLDateTime(new Date())
        }
    }, [])

    const submitOrShowModal = () => {
        if (isTeacher && !demoMode) return submitted()
        const form = document.getElementById("assessment-form") as HTMLFormElement
        setUnanswered(getUnansweredQuestions(form, drawingResponses))
        const formCompleteCheck  = checkIfAssessmentComplete(form, Object.keys(drawingResponses).length)
        if (!formCompleteCheck[0]) {
            setShowModal(true)
        } else {
            submitAssessment()
        }
    }

    const updateDrawingResponses = useCallback(({ questionNumber, answer, base64String, updateInitialState = false }: { questionNumber: number, answer: string, base64String: string, updateInitialState?: boolean }) => {
        setDrawingResponses(prev => ({ ...prev, [`question-${questionNumber}`]: { answer: answer, drawingResponse: base64String } }))
        if (updateInitialState) {
            setInitialAssessmentDrawingData?.(prev => ({ ...prev, [`question-${questionNumber}`]: { answer: answer, drawingResponse: base64String } }))
        }
    }, [setDrawingResponses, setInitialAssessmentDrawingData])

    const debouncedSaveAssessment = useCallback(async () => {
        if(!authContext.isLoggedIn || (isTeacher && !demoMode) || !authContext.trackingEnabled) return

        const now = new Date()
        if (now.getTime() - saveAssessmentTimerRef.current.getTime() < saveLessonInterval) {
            return
        }
        saveAssessmentTimerRef.current = now

        if (demoMode) {
            saveDemoAssessmentResponses({ drawingResponses: drawingResponses })
            return
        }

        const form = document.getElementById("assessment-form") as HTMLFormElement
        const postParams = {...parseFormData(form, false), ...drawingResponses, startTime: firstLoadTime.current}

        try {
            await modules.submitAssessment({
                moduleName: moduleName,
                assessmentType: type,
                assessmentNumber: assessmentNumber,
                submissionId: submissionId,
                postParams: postParams
            })
        } catch (err) {
            toast.error(err?.response?.data?.message ?? "There was an error auto saving the assessment. Please contact STEM Sims if the issue persists.")
        }
    }, [authContext.isLoggedIn, authContext.trackingEnabled, isTeacher, assessmentNumber, moduleName, submissionId, type, drawingResponses, demoMode])

    useEffect(() => {
        debouncedSaveAssessment()
    }, [debouncedSaveAssessment])

    if (submitting) return <div className="d-flex align-items-center justify-content-center vh-100"><BeatLoader /></div>

    const showSTEMCopyright =  BlocksWithSTEMCopyright.includes(block) && authContext.scienceBlocks.some(block => BlocksWithSTEMCopyright.includes(block))

    return <>
        <Modal show={showModal} centered size="lg" style={{ zIndex: 1061 }}>
            <Modal.Header>
                <Modal.Title className="w-100">Submit Assessment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Are you sure you want to submit assessment without completing all the questions?</p>
                <p className="text-muted">Unanswered questions: {unanswered?.map(item => item).join(", ")}</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="theme" onClick={() => setShowModal(false)}>No</Button>
                <Button variant="secondary" onClick={submitAssessment}>Yes</Button>
            </Modal.Footer>
        </Modal>

        <div className={styles.assignmentContents}>
            <div className="offset-lg-1 col-lg-10">
                <Assessment 
                    submissionId={submissionId}
                    initialAssessmentData={initialAssessmentData}
                    initialAssessmentDrawingData={initialAssessmentDrawingData}
                    updateDrawingResponses={updateDrawingResponses}
                    assessmentId={assessmentId}
                    moduleName={moduleName}
                    assessmentNumber={assessmentNumber} 
                    type={type}
                    isPartOfTopic={isPartOfTopic}
                    debouncedSaveAssessment={debouncedSaveAssessment}
                    oneQuestionMode={oneQuestionMode}
                    randomizable={randomizable}
                    submitEnabled={setSubmitEnabled}
                    isReview={isReview}
                />
                {(moduleName && moduleName.toLowerCase().match("saq")) && <>
                    {showSTEMCopyright ? 
                        <p className="font-weight-bold">© 2026 STEM Sims. All rights reserved.</p>
                        : <p className="font-weight-bold">This assessment includes copyrighted property of Science 21© 2024.</p>
                    }
                </>}
            </div>

            {!isPartOfTopic && 
                <Row className={`move-front align-items-center justify-content-center mt-4 mb-5`}>
                    {!assessmentOnly &&
                        <div className="col-sm-12 col-lg-8 d-flex justify-content-center">
                            <AssignmentProgress active="assessment" />
                        </div>}

                    {(authContext.isStudent || demoMode || !assessmentOnly) &&
                        <div className="col-sm-12 col-lg-4 d-flex justify-content-center">
                            <Button className={`btn-lg ${submitEnabled ? "" : "d-none"}`} id="assessment-submit" disabled={submitting} onClick={submitOrShowModal}>
                                {(isTeacher && !demoMode) ? "Return to Lesson" : "Submit Assessment"}
                            </Button>
                        </div>}
                </Row>}
        </div>
    </>
}
